import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'antd';
import styled from 'styled-components';
import {
  useDataObjectVersionIndicatorQuery,
  useQueryParams,
  Padding,
  PopupView,
  DataObjectVersionIndicatorRequestParams,
  formatDate,
  Detail,
  Spinner,
  VersionDisplay,
  DataVersionStatus,
  useSystemClassifierItemsBy,
  ButtonGroupNav,
  TotalEntries,
  DashboardType,
} from '@atlas-frontend/sdk';
import { AlgorithmsModal, type ModalData } from '../modals/DetailsModal';
import { path } from '../../../paths';
import { useNumericParam } from '../../../common/useEntityIdParam';
import { withParams } from '../../../paths/withParams';
import { NotFound } from '../../NotFound';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ReportIndicatorAlgorithmsTable } from './ReportIndicatorAlgorithmsTable';
import { ReportIndicatorConfidentialityObjectsTable } from './ReportIndicatorConfidentialityObjectsTable';
import translations from './ReportIndicatorValue.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'report-indicator-value',
);
addTranslations(translations);

export const ReportIndicatorValue: FC = () => {
  const dataObjectVersionId = useNumericParam('dataObjectVersionId');
  const { indicatorCode } = useParams<{ indicatorCode?: string }>();
  const [{ key }] = useQueryParams();

  if (!key || !indicatorCode || !dataObjectVersionId) {
    return <NotFound />;
  }

  return (
    <ReportIndicatorValueInner
      params={{ dataObjectVersionId, indicatorCode, key }}
    />
  );
};

interface ReportIndicatorValueInnerProps {
  params: DataObjectVersionIndicatorRequestParams;
}

const ReportIndicatorValueInner: FC<ReportIndicatorValueInnerProps> = (
  props,
) => {
  const { params } = props;
  const { t } = useTranslation();
  const { data: indicatorInfo, isLoading } = useDataObjectVersionIndicatorQuery(
    params,
    {
      throwOnError: true,
    },
  );

  const confidentialityStatusesByCode = useSystemClassifierItemsBy(
    ['CONFIDENTIALITY_STATUS'],
    'code',
  ).CONFIDENTIALITY_STATUS;

  const [{ segment }] = useQueryParams();
  const currentSegment = segment || 'algorithms';

  const [clickedAlgorithm, setClickedAlgorithm] = useState<
    { code: string; targetSequenceNumber: number | null } | undefined
  >();
  const isAlgorithmsModalOpen = clickedAlgorithm !== undefined;

  const algorithmsModalData: ModalData | null = indicatorInfo
    ? {
        type: DashboardType.REPORT,
        version: indicatorInfo.dataObjectVersion,
        descriptionOption: {
          label: indicatorInfo.dataObjectDescriptionCodeName,
        } as unknown as ModalData['descriptionOption'],
      }
    : null;

  return (
    <>
      {isAlgorithmsModalOpen && algorithmsModalData && (
        <AlgorithmsModal
          {...algorithmsModalData}
          initialAlgorithmCode={clickedAlgorithm?.code}
          initialAlgorithmTargetSequenceNumber={
            clickedAlgorithm?.targetSequenceNumber
          }
          onClose={() => setClickedAlgorithm(undefined)}
        />
      )}
      <PopupView
        title={t('report-value')}
        footer={
          currentSegment === 'algorithms' && indicatorInfo?.algorithms ? (
            <TotalEntries count={indicatorInfo.algorithms.length} />
          ) : currentSegment === 'confidentialityObjects' &&
            indicatorInfo?.confidentialityObjects ? (
            <TotalEntries count={indicatorInfo.confidentialityObjects.length} />
          ) : null
        }
      >
        {isLoading && <Spinner overlay />}
        {indicatorInfo && (
          <Padding horizontal bottom>
            <Flex vertical gap="large">
              <h4>{indicatorInfo.dataObjectDescriptionCodeName}</h4>
              <div>
                <Columns>
                  <Detail label={t('field.valueDate.label')}>
                    {formatDate(indicatorInfo.dataObjectVersion.valueDate)}
                  </Detail>
                  <Detail label={t('field.version.label')}>
                    <VersionDisplay
                      versionNumber={
                        indicatorInfo.dataObjectVersion.versionNumber
                      }
                      minorNumber={indicatorInfo.dataObjectVersion.minorNumber}
                      status={indicatorInfo.dataObjectVersion.statusCode!}
                    >
                      <DataVersionStatus
                        status={indicatorInfo.dataObjectVersion.statusCode!}
                      />
                    </VersionDisplay>
                  </Detail>
                  <Detail label={t('field.confidentialityStatus.label')}>
                    {
                      confidentialityStatusesByCode.get(
                        indicatorInfo.confidentialityStatus,
                      )!.name
                    }
                  </Detail>
                  <Detail label={t('field.indicator.label')}>
                    {`${indicatorInfo.indicator.code} - ${indicatorInfo.indicator.name}`}
                  </Detail>
                  <Detail label={t('field.indicatorValue.label')}>
                    {indicatorInfo.value}
                  </Detail>
                  <Detail label={t('field.indicatorStatus.label')}>
                    {indicatorInfo.indicatorStatus || ''}
                  </Detail>
                  <Detail
                    label={t('field.indicatorConfidentialityStatus.label')}
                  >
                    {indicatorInfo.indicatorConfidentialityStatus || ''}
                  </Detail>
                </Columns>
                <Detail label={t('field.key.label')}>{params.key}</Detail>
              </div>
              <ButtonGroupNav
                items={[
                  {
                    key: 'algorithms',
                    label: t('segment.algorithms', {
                      count: indicatorInfo.algorithms.length,
                    }),
                    to: withParams(path.visualValidationReportIndicatorValue, {
                      ...params,
                      segment: 'algorithms',
                    }),
                    disabled: !params.indicatorCode,
                  },
                  {
                    key: 'confidentialityObjects',
                    label: t('segment.confidentialityObjects', {
                      count: indicatorInfo.confidentialityObjects.length,
                    }),
                    to: withParams(path.visualValidationReportIndicatorValue, {
                      ...params,
                      segment: 'confidentialityObjects',
                    }),
                    disabled: !params.indicatorCode,
                  },
                ]}
                currentKey={currentSegment}
              />
              {currentSegment === 'algorithms' ? (
                <ReportIndicatorAlgorithmsTable
                  dataObjectDescriptionVersionId={
                    indicatorInfo.dataObjectDescriptionVersionId
                  }
                  algorithms={indicatorInfo.algorithms}
                  onAlgorithmCodeClick={(code, targetSequenceNumber) =>
                    setClickedAlgorithm({ code, targetSequenceNumber })
                  }
                />
              ) : currentSegment === 'confidentialityObjects' ? (
                <ReportIndicatorConfidentialityObjectsTable
                  confidentialityObjects={indicatorInfo.confidentialityObjects}
                />
              ) : null}
            </Flex>
          </Padding>
        )}
      </PopupView>
    </>
  );
};

const Columns = styled.div`
  columns: 20em auto;
  max-width: 50rem;
`;
